import { LoaderFunctionArgs, redirect } from "react-router-dom";

import { getDatefromWeekNum, myAxios, MyDate, weeknum } from "src/components/common/functions";

const allowedSearchKeys = ["status", "showTimes", "showHours", "category", "laborCategory", "q", "view", "date", "users"] as const;
const allowedViews = new Set(["month", "week", "day"]);

export async function calendarLoader({ params, request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    let shouldRedirect = false;

    for (const [key] of url.searchParams) {
        if (!allowedSearchKeys.includes(key as typeof allowedSearchKeys[number])) {
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }

    if (!url.searchParams.get("status")) {
        url.searchParams.set("status", "All");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("showTimes")) {
        url.searchParams.set("showTimes", "0");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("showHours")) {
        url.searchParams.set("showHours", "0");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("category")) {
        url.searchParams.set("category", "All");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("laborCategory")) {
        url.searchParams.set("laborCategory", "All");
        shouldRedirect = true;
    }
    if (!url.searchParams.has("q")) {
        url.searchParams.set("q", "");
        shouldRedirect = true;
    }
    if (!url.searchParams.has("users")) {
        url.searchParams.set("users", "");
        shouldRedirect = true;
    }

    const weekRes = await myAxios({ fileName: "projectCalendar/readLaborAssignmentsWeek.php" });
    if (!weekRes) throw redirect("..");

    const minTime = weekRes.data?.minTime;
    const maxTime = weekRes.data?.maxTime;
    if (!minTime || !maxTime) {
        return { weeks: [], selectedWeek: "", activeDate: "", view: "week", rangeStart: "", rangeEnd: "", assignments: [] };
    }

    const dateArray: string[] = [];
    const dateMin = new Date(String(minTime).replace(/-/g, "/"));
    const dateMax = new Date(String(maxTime).replace(/-/g, "/"));
    const latestWeek = weeknum(dateMax);
    while (dateMin < dateMax) {
        dateArray.push(weeknum(dateMax));
        dateMax.setDate(dateMax.getDate() - 7);
    }
    if (!dateArray.includes(weeknum(dateMin))) {
        dateArray.push(weeknum(dateMin));
    }

    const urlView = url.searchParams.get("view") ?? "week";
    const view = allowedViews.has(urlView) ? urlView : "week";
    if (urlView !== view) {
        url.searchParams.set("view", view);
        shouldRedirect = true;
    }

    const selectedWeek = params.week && dateArray.includes(params.week) ? params.week : latestWeek;
    const fallbackDate = getDatefromWeekNum(selectedWeek, 0) ?? "";
    const requestedDate = normalizeDate(url.searchParams.get("date")) ?? fallbackDate;
    const derivedWeek = requestedDate ? weeknum(requestedDate) : selectedWeek;
    const normalizedWeek = dateArray.includes(derivedWeek) ? derivedWeek : selectedWeek;
    const activeDate = requestedDate && dateArray.includes(derivedWeek)
        ? requestedDate
        : getDatefromWeekNum(normalizedWeek, 0) ?? fallbackDate;

    if (url.searchParams.get("date") !== activeDate) {
        url.searchParams.set("date", activeDate);
        shouldRedirect = true;
    }

    if (!params.week || params.week !== normalizedWeek || shouldRedirect) {
        throw redirect(`/Calendar/${normalizedWeek}?${url.searchParams.toString()}`);
    }

    const { rangeStart, rangeEnd } = getRangeFromView(view, activeDate, normalizedWeek);
    const formData = new FormData();
    formData.append("weekStart", rangeStart);
    formData.append("weekEnd", rangeEnd);
    const assignmentsRes = await myAxios({ fileName: "projectCalendar/readLaborAssignments.php", formData });
    if (!assignmentsRes) throw redirect("..");

    return {
        weeks: dateArray,
        selectedWeek: normalizedWeek,
        activeDate,
        view,
        rangeStart,
        rangeEnd,
        assignments: assignmentsRes.data ?? [],
    };
}

function normalizeDate(value: string | null) {
    if (!value) return null;
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

function getRangeFromView(view: string, activeDate: string, selectedWeek: string) {
    if (view === "day") {
        return { rangeStart: activeDate, rangeEnd: activeDate };
    }
    if (view === "month") {
        const date = new MyDate(activeDate.replace(/-/g, "/"));
        const rangeStart = new MyDate(date.getFullYear(), date.getMonth(), 1).s();
        const rangeEnd = new MyDate(date.getFullYear(), date.getMonth() + 1, 0).s();
        return { rangeStart, rangeEnd };
    }
    return {
        rangeStart: getDatefromWeekNum(selectedWeek, 0) ?? activeDate,
        rangeEnd: getDatefromWeekNum(selectedWeek, 6) ?? activeDate,
    };
}
